/**
 * Opportunity Lifecycle Timeline (History tab, top section).
 * Renders the full chronological state transition log for a single opportunity.
 * Never manufactures events for legacy opportunities — shows a labelled empty
 * state instead.
 */
import { useMemo } from "react";
import {
  Sparkles, Eye, Bookmark, Radar, Play, Loader2, CheckCircle2,
  FileText, ThumbsUp, Send, LineChart, Trophy, TrendingDown,
  Minus, Clock, Layers, XCircle,
} from "lucide-react";

const STATE_META = {
  NEW:                     { icon: Sparkles,     tone: "text-signal-blue",   label: "Discovered" },
  VIEWED:                  { icon: Eye,          tone: "text-muted-foreground", label: "Viewed" },
  SAVED:                   { icon: Bookmark,     tone: "text-signal-yellow", label: "Saved" },
  WATCHING:                { icon: Radar,        tone: "text-signal-yellow", label: "Watching" },
  FULL_ANALYSIS_REQUESTED: { icon: Play,         tone: "text-signal-blue",   label: "Analysis Requested" },
  FULL_ANALYSIS_RUNNING:   { icon: Loader2,      tone: "text-signal-blue",   label: "Analysis Running" },
  FULL_ANALYSIS_COMPLETED: { icon: CheckCircle2, tone: "text-signal-blue",   label: "Analysis Completed" },
  PROPOSAL_CREATED:        { icon: FileText,     tone: "text-signal-blue",   label: "Proposal Created" },
  APPROVED:                { icon: ThumbsUp,     tone: "pnl-up",             label: "Approved" },
  PAPER_ORDER_SUBMITTED:   { icon: Send,         tone: "pnl-up",             label: "Order Submitted" },
  PAPER_POSITION_OPEN:     { icon: LineChart,    tone: "pnl-up",             label: "Position Open" },
  CLOSED_WINNER:           { icon: Trophy,       tone: "pnl-up",             label: "Closed · Winner" },
  CLOSED_LOSER:            { icon: TrendingDown, tone: "pnl-down",           label: "Closed · Loser" },
  CLOSED_BREAKEVEN:        { icon: Minus,        tone: "text-muted-foreground", label: "Closed · Breakeven" },
  EXPIRED:                 { icon: Clock,        tone: "text-muted-foreground", label: "Expired" },
  SUPERSEDED:              { icon: Layers,       tone: "text-muted-foreground", label: "Superseded" },
  DISMISSED:               { icon: XCircle,      tone: "text-muted-foreground", label: "Dismissed" },
};

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const d = Date.now() - t;
  if (isNaN(d)) return "";
  const s = Math.round(d / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function ActorChip({ e }) {
  const map = {
    USER: "border-signal-blue text-signal-blue",
    AI: "border-signal-yellow text-signal-yellow",
    WORKER: "border-muted-foreground text-muted-foreground",
    SYSTEM: "border-muted-foreground text-muted-foreground",
  };
  const cls = map[e.actor_type] || "border-muted-foreground text-muted-foreground";
  return (
    <span
      className={`chip !text-[9px] border ${cls}`}
      title={e.actor_id ? `${e.actor_type} · ${e.actor_id}` : e.actor_type}
    >{(e.actor_type || "").toLowerCase()}</span>
  );
}

export default function OpportunityLifecycleTimeline({
  events = [],
  isLegacy = false,
  opportunityCreatedAt = null,
}) {
  const sorted = useMemo(() => {
    return [...(events || [])].sort((a, b) => {
      const ta = new Date(a.timestamp || 0).getTime();
      const tb = new Date(b.timestamp || 0).getTime();
      return ta - tb;
    });
  }, [events]);

  if (sorted.length === 0) {
    return (
      <div className="border border-border/40 p-3" data-testid="opp-lifecycle-empty">
        <div className="label-cap mb-1">Opportunity Lifecycle</div>
        <p className="text-[11px] text-muted-foreground italic">
          {isLegacy
            ? "Legacy record — lifecycle tracking began after this opportunity was created."
            : "Earlier lifecycle data unavailable."}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="opp-lifecycle-timeline">
      <div className="flex items-baseline justify-between mb-2">
        <div className="label-cap">Opportunity Lifecycle</div>
        <span className="text-[10px] text-muted-foreground mono">{sorted.length} events</span>
      </div>
      <ol className="relative border-l border-border/60 ml-2 space-y-3">
        {sorted.map((e, idx) => {
          const meta = STATE_META[e.new_state] || {
            icon: Clock, tone: "text-muted-foreground",
            label: e.new_state?.replace(/_/g, " ") || "Event",
          };
          const Icon = meta.icon;
          const isRunning = e.new_state === "FULL_ANALYSIS_RUNNING";
          return (
            <li
              key={e.id || idx}
              className="ml-3 pl-3"
              data-testid={`opp-lifecycle-event-${e.new_state?.toLowerCase()}`}
            >
              <span
                className={`absolute -left-[9px] w-4 h-4 border border-border/70 bg-background flex items-center justify-center ${meta.tone}`}
                aria-hidden
              >
                <Icon size={9} className={isRunning ? "animate-spin" : ""} />
              </span>
              <div className="flex items-baseline flex-wrap gap-2">
                <span className={`mono uppercase text-[11px] ${meta.tone}`}>
                  {meta.label}
                </span>
                <ActorChip e={e} />
                <span
                  className="mono text-[10px] text-muted-foreground ml-auto"
                  title={fmtTs(e.timestamp)}
                >{relTime(e.timestamp)}</span>
              </div>
              {e.explanation && (
                <p className="text-[11px] text-foreground/80 mt-0.5 leading-snug">
                  {e.explanation}
                </p>
              )}
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-muted-foreground mono">
                {e.reason_code && <span>reason {e.reason_code}</span>}
                {e.verdict_at_time && <span>verdict {e.verdict_at_time}</span>}
                {typeof e.score_at_time === "number" && (
                  <span>score {e.score_at_time}</span>
                )}
                {typeof e.price_at_time === "number" && (
                  <span>${e.price_at_time.toFixed(2)}</span>
                )}
                {e.market_regime && <span>regime {e.market_regime}</span>}
                {e.scan_profile_id && (
                  <span>
                    profile {e.scan_profile_id.slice(0, 8)}
                    {e.scan_profile_version ? ` v${e.scan_profile_version}` : ""}
                  </span>
                )}
                {e.linked_entity_type && e.linked_entity_id && (
                  <span>
                    {e.linked_entity_type} {e.linked_entity_id.slice(0, 8)}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {opportunityCreatedAt && (
        <p className="text-[10px] text-muted-foreground italic mt-2">
          Opportunity created {fmtTs(opportunityCreatedAt)}
        </p>
      )}
    </div>
  );
}
